'use client';

import { useEffect, useRef } from 'react';

import { activeFilters } from '@eliadmin/core/src/components/dlz/category/active-filters';
import { FilterChip } from '@eliadmin/core/src/components/dlz/ui/chip';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const NUMBER = new Intl.NumberFormat('sk-SK', { maximumFractionDigits: 2 });

/** 0 is an open bound: only a lower bound is set when the maximum is 0. */
const priceRange = (min: number, max: number) =>
  max ? `${NUMBER.format(min)} – ${NUMBER.format(max)}` : `od ${NUMBER.format(min)}`;

/**
 * Figma „Active filters“: one chip per selected value and the „Zrušiť filtre“ link, 12 px apart in
 * one wrapping row (8 px on phones, 60438:86563). Removing a chip moves the focus to the chip that
 * takes its place.
 */
export function FilterChips({ className }: { className?: string }) {
  const { t } = useTranslations();
  const category = useCategoryStore(s => s.category);
  const filter = useCategoryStore(s => s.filter);
  const setFilter = useCategoryStore(s => s.setFilter);
  const clearFilters = useCategoryStore(s => s.clearFilters);
  const currency = useGlobalStore(s => s.generalLayout?.currency);
  const listRef = useRef<HTMLUListElement>(null);
  const focusIndex = useRef<number | null>(null);

  // The admin places the currency symbol before prices („€ 0,00“); the chip follows Figma.
  const chips = activeFilters(category, filter, {
    price: (min, max) =>
      [`${t('text164')}:`, priceRange(min, max), currency].filter(Boolean).join(' '),
    discounted: 'Iba v akcii',
  });

  useEffect(() => {
    if (focusIndex.current === null) return;
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('li > span > button');
    buttons?.[Math.min(focusIndex.current, buttons.length - 1)]?.focus();
    focusIndex.current = null;
  }, [chips.length]);

  if (!chips.length) return null;

  return (
    <ul
      ref={listRef}
      aria-label="Aktívne filtre"
      className={cn('flex flex-wrap items-center gap-2 md:gap-3', className)}
    >
      {chips.map((chip, index) => (
        <li key={chip.key}>
          <FilterChip
            removeLabel={`Zrušiť filter ${chip.label}`}
            onRemove={() => {
              focusIndex.current = index;
              setFilter(chip.remove);
            }}
          >
            {chip.label}
          </FilterChip>
        </li>
      ))}
      <li>
        <button
          type="button"
          onClick={clearFilters}
          className="dlz-body-s-medium rounded-sm text-[#D7191F] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
        >
          Zrušiť filtre
        </button>
      </li>
    </ul>
  );
}
